import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './Pricing.module.css';

const Pricing = () => {
  const [ref, inView] = useInView({ 
    triggerOnce: true, 
    threshold: 0.15 
  });

  const plans = [
    {
      name: 'Starter',
      price: '500',
      period: '/month',
      description: 'For small SACCOs and MFIs getting started',
      features: [
        'Up to 500 assessments/month',
        'Credit Scoring Engine',
        'Share Valuation Engine',
        'Basic analytics dashboard',
        'Email support',
        'API access',
        'Custom integrations'
      ],
      included: [true, true, true, true, true, false, false],
      cta: 'Start Trial'
    },
    {
      name: 'Professional',
      price: '2,500',
      period: '/month',
      description: 'For growing institutions with higher volume',
      features: [
        'Up to 2,500 assessments/month',
        'Credit Scoring Engine',
        'Share Valuation Engine',
        'Advanced analytics',
        'Priority support 24/7',
        'API access',
        'Custom integrations'
      ],
      included: [true, true, true, true, true, true, true],
      cta: 'Start Trial',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For banks and large financial institutions',
      features: [
        'Unlimited assessments',
        'Credit Scoring Engine',
        'Share Valuation Engine',
        'Custom analytics',
        'Dedicated support team',
        'Full API access',
        'White-label integration'
      ],
      included: [true, true, true, true, true, true, true],
      cta: 'Contact Sales'
    }
  ];

  return (
    <section className={styles.section} id="pricing" data-section="pricing" ref={ref}>
      <div className={styles.container}>
        
        <motion.div 
          className={styles.header}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className={styles.sectionLabel}>Pricing</div>
          <h2 className={styles.title}>Simple, Transparent Pricing</h2>
          <p className={styles.subtitle}>
            Choose the plan that fits your institution's needs
          </p>
        </motion.div>

        <div className={styles.pricingGrid}>
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`${styles.pricingCard} ${plan.popular ? styles.popular : ''}`}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {plan.popular && (
                <div className={styles.popularBadge}>Most Popular</div>
              )}

              <div className={styles.cardHeader}>
                <h3 className={styles.planName}>{plan.name}</h3>
                <div className={styles.priceWrapper}>
                  {plan.price === 'Custom' ? (
                    <div className={styles.customPrice}>Let's Talk</div>
                  ) : (
                    <>
                      <span className={styles.currency}>$</span>
                      <span className={styles.price}>{plan.price}</span>
                      <span className={styles.period}>{plan.period}</span>
                    </>
                  )}
                </div>
                <p className={styles.planDescription}>{plan.description}</p>
              </div>

              <button className={styles.ctaButton}>{plan.cta}</button>

              <ul className={styles.featuresList}>
                {plan.features.map((feature, i) => (
                  <li key={i} className={`${styles.feature} ${!plan.included[i] ? styles.disabled : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.featureIcon}>
                      {plan.included[i] ? (
                        <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      ) : (
                        <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      )}
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>


      </div>
    </section>
  );
};

export default Pricing;